/**
 * Zustand Store for Sol Invictus Visualization
 * Global state management for UI and scene controls
 */

import { create } from 'zustand';

interface TelemetryData {
  swarmTemperature: number;      // Kelvin
  powerOutput: number;           // Petawatts
  systemEfficiency: number;      // Percentage
  activeSwarmUnits: number;
  laserTransmissionRate: number; // TW
  mercuryRelayStatus: 'active' | 'standby' | 'offline';
}

// Camera target types for focus mode
export type CameraTarget =
  | 'sun'
  | 'mercury'
  | 'earth'
  | 'eahp'
  | 'geoReceiver'
  | 'starship'
  | null;

// Camera modes
export type CameraMode =
  | 'free'        // Free orbit around scene center
  | 'focus'       // Orbit around selected object
  | 'pov'         // First-person from inside Starship
  | 'chase'       // Third-person behind Starship
  | 'cinematic';  // Automatic cinematic movements

interface ViewState {
  showLasers: boolean;
  showOrbits: boolean;
  showHabitableZone: boolean;
  showLabels: boolean;
  showLDRParticles: boolean;
  showEAHPSwarm: boolean;
  selectedObject: string | null;
  cameraMode: CameraMode;
  cameraTarget: CameraTarget;
  followShipIndex: number; // Which Starship to follow (0-based)
}

// Inspector Mode data
export interface InspectedObjectData {
  id: string;
  name: string;
  type: string;
  material: string;
  thermalLoad: string;
  powerOutput: string;
  description: string;
  specifications: Record<string, string>;
}

interface StoreState {
  // Telemetry
  telemetry: TelemetryData;
  updateTelemetry: (data: Partial<TelemetryData>) => void;

  // View controls
  view: ViewState;
  toggleLasers: () => void;
  toggleOrbits: () => void;
  toggleHabitableZone: () => void;
  toggleLabels: () => void;
  toggleLDRParticles: () => void;
  toggleEAHPSwarm: () => void;
  setSelectedObject: (id: string | null) => void;
  setCameraMode: (mode: CameraMode) => void;
  setCameraTarget: (target: CameraTarget) => void;
  setFollowShipIndex: (index: number) => void;
  nextShip: () => void;
  prevShip: () => void;

  // Loading state
  isLoading: boolean;
  loadingProgress: number;
  loadingStatus: string;
  setLoading: (loading: boolean, progress?: number, status?: string) => void;

  // Animation controls
  animationSpeed: number;
  setAnimationSpeed: (speed: number) => void;
  isPaused: boolean;
  togglePause: () => void;

  // UI state
  showInfoPanel: boolean;
  toggleInfoPanel: () => void;
  activeTab: 'overview' | 'eahp' | 'technology' | 'comparison';
  setActiveTab: (tab: 'overview' | 'eahp' | 'technology' | 'comparison') => void;

  // Music Video Clip Mode
  isClipMode: boolean;
  clipProgress: number;
  clipShotName: string;
  startClipMode: () => void;
  stopClipMode: () => void;
  updateClipProgress: (progress: number, shotName: string) => void;

  // Inspector Mode
  inspectedObject: InspectedObjectData | null;
  setInspectedObject: (obj: InspectedObjectData | null) => void;
  closeInspector: () => void;
}

export const useStore = create<StoreState>((set) => ({
  // Initial telemetry values
  telemetry: {
    swarmTemperature: 880, // K at 0.1 AU
    powerOutput: 0.1,      // Starting low, will increase
    systemEfficiency: 30,  // DSPL efficiency
    activeSwarmUnits: 500,
    laserTransmissionRate: 0.03, // TW
    mercuryRelayStatus: 'active',
  },

  updateTelemetry: (data) =>
    set((state) => ({
      telemetry: { ...state.telemetry, ...data },
    })),

  // Initial view state
  view: {
    showLasers: true,
    showOrbits: true,
    showHabitableZone: false,
    showLabels: true,
    showLDRParticles: true,
    showEAHPSwarm: true,
    selectedObject: null,
    cameraMode: 'free',
    cameraTarget: null,
    followShipIndex: 0,
  },

  toggleLasers: () =>
    set((state) => ({
      view: { ...state.view, showLasers: !state.view.showLasers },
    })),

  toggleOrbits: () =>
    set((state) => ({
      view: { ...state.view, showOrbits: !state.view.showOrbits },
    })),

  toggleHabitableZone: () =>
    set((state) => ({
      view: { ...state.view, showHabitableZone: !state.view.showHabitableZone },
    })),

  toggleLabels: () =>
    set((state) => ({
      view: { ...state.view, showLabels: !state.view.showLabels },
    })),

  toggleLDRParticles: () =>
    set((state) => ({
      view: { ...state.view, showLDRParticles: !state.view.showLDRParticles },
    })),

  toggleEAHPSwarm: () =>
    set((state) => ({
      view: { ...state.view, showEAHPSwarm: !state.view.showEAHPSwarm },
    })),

  setSelectedObject: (id) =>
    set((state) => ({
      view: { ...state.view, selectedObject: id },
    })),

  setCameraMode: (mode) =>
    set((state) => ({
      view: { ...state.view, cameraMode: mode },
    })),

  setCameraTarget: (target) =>
    set((state) => ({
      view: { ...state.view, cameraTarget: target },
    })),

  setFollowShipIndex: (index) =>
    set((state) => ({
      view: { ...state.view, followShipIndex: index },
    })),

  nextShip: () =>
    set((state) => ({
      view: { ...state.view, followShipIndex: (state.view.followShipIndex + 1) % 4 },
    })),

  prevShip: () =>
    set((state) => ({
      view: { ...state.view, followShipIndex: (state.view.followShipIndex - 1 + 4) % 4 },
    })),

  // Loading state
  isLoading: true,
  loadingProgress: 0,
  loadingStatus: 'Initializing...',

  setLoading: (loading, progress = 0, status = '') =>
    set({
      isLoading: loading,
      loadingProgress: progress,
      loadingStatus: status,
    }),

  // Animation
  animationSpeed: 1,
  setAnimationSpeed: (speed) => set({ animationSpeed: speed }),
  isPaused: false,
  togglePause: () => set((state) => ({ isPaused: !state.isPaused })),

  // UI state
  showInfoPanel: true,
  toggleInfoPanel: () =>
    set((state) => ({ showInfoPanel: !state.showInfoPanel })),
  activeTab: 'overview',
  setActiveTab: (tab) => set({ activeTab: tab }),

  // Music Video Clip Mode
  isClipMode: false,
  clipProgress: 0,
  clipShotName: '',
  startClipMode: () => set({ isClipMode: true, clipProgress: 0, clipShotName: '', isPaused: false }),
  stopClipMode: () => set({ isClipMode: false, clipProgress: 0, clipShotName: '' }),
  updateClipProgress: (progress, shotName) => set({ clipProgress: progress, clipShotName: shotName }),

  // Inspector Mode
  inspectedObject: null,
  setInspectedObject: (obj) => set({ inspectedObject: obj }),
  closeInspector: () => set({ inspectedObject: null }),
}));

// Predefined object data for inspector
export const INSPECTABLE_OBJECTS: Record<string, InspectedObjectData> = {
  mercuryRelay: {
    id: 'mercuryRelay',
    name: 'Mercury Optical Relay Station',
    type: 'Optical Relay Mirror',
    material: 'Polished Beryllium / Gold-Coated Optics',
    thermalLoad: '4,500 K (operational)',
    powerOutput: '0 (passive relay)',
    description: 'High-precision optical relay station positioned in Mercury orbit. Uses adaptive optics to redirect and focus incoming laser beams from the EAHP swarm toward Earth-based receivers.',
    specifications: {
      'Orbit': '0.39 AU (Mercury L1)',
      'Primary Mirror': '2.4m Beryllium',
      'Pointing Accuracy': '0.001 arcsec',
      'Thermal Control': 'Liquid Metal LDR',
      'Relay Capacity': '500 TW optical',
      'Redundancy': 'Dual-mirror failover',
    },
  },
  geoReceiver: {
    id: 'geoReceiver',
    name: 'GEO Power Receiver Station',
    type: 'Rectenna Array',
    material: 'Copper/GaAs Photovoltaic Grid',
    thermalLoad: '380 K (operational)',
    powerOutput: '50 GW (to ground)',
    description: 'Geostationary orbital power station that receives focused laser beams and converts them to microwave power for transmission to Earth surface rectenna farms.',
    specifications: {
      'Orbit': '35,786 km GEO',
      'Receiver Area': '1 km diameter',
      'Conversion Efficiency': '85%',
      'Microwave Frequency': '5.8 GHz',
      'Ground Station': 'Rectenna Farm',
      'Uptime': '99.97%',
    },
  },
  eahpSwarm: {
    id: 'eahpSwarm',
    name: 'EAHP Satellite Swarm',
    type: 'Direct Solar-Pumped Laser Array',
    material: 'Nd:YAG Crystal / Carbon Composite',
    thermalLoad: '880 K (peak)',
    powerOutput: '100 MW per unit',
    description: 'Enxame Ativo Helios-Proximal: A constellation of 5,000+ satellites at 0.1 AU that use direct solar-pumped lasers to convert intense sunlight into coherent beams.',
    specifications: {
      'Orbit': '0.1 AU (15M km)',
      'Units': '5,000+',
      'Aperture': '10m per unit',
      'Laser Type': 'Nd:YAG DSPL',
      'Efficiency': '30% solar-to-laser',
      'Total Output': '500+ TW',
    },
  },
};
